import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

const SEEN_KEY = "rsoc_tour_seen_v1";
const SEEN_ALIASES = ["rsoc_tour_seen_v1", "tour_seen"]; // both honored

function _tourAlreadySeen() {
  return SEEN_ALIASES.some((k) => {
    const v = localStorage.getItem(k);
    return v === "1" || v === "true";
  });
}

function _cleanupStaleDriver() {
  // Defensive: if driver.js left an overlay behind (unmount race, hot-reload),
  // wipe it so it doesn't intercept clicks on the real UI.
  document.querySelectorAll(
    ".driver-overlay, .driver-popover, [class^='driver-'], [class*=' driver-']"
  ).forEach((el) => el.remove());
  document.body.classList.remove("driver-active", "driver-fade");
}

/**
 * One-time product tour shown to new buyers after first login.
 * Steps walk through the highest-leverage pages (Dashboard → Builder → Optimize
 * Auto-Operator → Intelligence → Digest → Docs). Uses sidebar nav selectors so
 * it survives page-level refactors. Users can skip or replay from the Docs page.
 */
export default function ProductTour({ forceShow = false, onDone }) {
  const navigate = useNavigate();
  const location = useLocation();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    if (_tourAlreadySeen() && !forceShow) {
      // Guard: kill any stale overlay that a previous render/hot-reload left.
      _cleanupStaleDriver();
      return;
    }

    // Only start when sidebar has rendered
    const root = document.querySelector('[data-testid="nav-performance"]');
    if (!root) return;

    started.current = true;

    const d = driver({
      showProgress: true,
      allowClose: true,
      animate: true,
      overlayOpacity: 0.55,
      smoothScroll: true,
      stagePadding: 6,
      stageRadius: 8,
      popoverClass: "rsoc-tour",
      progressText: "{{current}} / {{total}}",
      nextBtnText: "Next →",
      prevBtnText: "← Back",
      doneBtnText: "Get started",
      onDestroyed: () => {
        localStorage.setItem(SEEN_KEY, "1");
        onDone && onDone();
      },
      steps: [
        {
          element: '[data-testid="nav-performance"]',
          popover: {
            title: "👋 Welcome to RSOC Hub",
            description:
              "A quick 6-step tour so you know where the money-making features live. Click Next — or Skip at any time.",
            side: "right",
            align: "start",
          },
        },
        {
          element: '[data-testid="nav-builder"]',
          popover: {
            title: "Campaign Builder",
            description:
              "Turn a one-line brief into a full campaign — AI copy, images, video slideshow, and the <b>Matrix Launch</b> button that fans one brief into 24 test variants for $30.",
            side: "right",
          },
          onHighlighted: () => navigate("/dashboard"),
        },
        {
          element: '[data-testid="nav-optimize"]',
          popover: {
            title: "Optimize — your biggest ROI lever",
            description:
              "Six operator tactics in one page. Top of the page has the <b>Auto-Operator</b> switch — arm it once and fatigue rotation + winner scaling run nightly while you sleep.",
            side: "right",
          },
        },
        {
          element: '[data-testid="nav-intelligence"]',
          popover: {
            title: "Intelligence — the app learns",
            description:
              "Five self-learning tactics: Creative DNA, RSOC Keyword ranking, Attribution leak detection, Micro-vertical discovery, and LP↔Ad emotional match scoring.",
            side: "right",
          },
        },
        {
          element: '[data-testid="nav-digest"]',
          popover: {
            title: "Daily Digest email",
            description:
              "One email every morning with yesterday's P&L + overnight automation + <b>one-tap Scale / Pause buttons</b> right inside the email. Read on the subway, account optimized before you reach the office.",
            side: "right",
          },
        },
        {
          element: '[data-testid="nav-docs"]',
          popover: {
            title: "Docs & help bubbles",
            description:
              "Every feature has a <span style='color:#2563eb;font-weight:600;'>?</span> bubble next to it — click for a why/how pop-up. Full reference lives here in Docs. You're ready — go ship profit.",
            side: "right",
          },
        },
      ],
    });

    // Small delay so layout stabilizes after login redirect
    setTimeout(() => d.drive(), 450);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceShow, location.pathname]);

  return null;
}

// Utility to reset + replay from any other component.
export function restartTour() {
  localStorage.removeItem(SEEN_KEY);
  window.location.reload();
}
